// client/src/pages/business/Dashboard.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/common/Navbar';
import StorefrontBanner from '../../components/common/StorefrontBanner';
import Footer from '../../components/common/Footer';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { businessAPI, ordersAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { format } from 'date-fns';

const BusinessDashboard = () => {
  const { t } = useTranslation(['business', 'orders']);
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState({ totalOrders: 0, totalSpent: 0, totalSuppliers: 0 });
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        
        const [analyticsRes, ordersRes] = await Promise.all([
          businessAPI.getAnalytics(),
          ordersAPI.getMyOrders()
        ]);


        if (analyticsRes?.data?.stats) {
          setAnalytics(analyticsRes.data.stats);
        }

        // ✅ FIXED: Handle new validated API format { success: true, data: [...] }
        if (ordersRes?.data) {
          let ordersList = [];
          
          // Check new format first: { success: true, data: [...] }
          if (ordersRes.data.data && Array.isArray(ordersRes.data.data)) {
            ordersList = ordersRes.data.data;
          } else if (Array.isArray(ordersRes.data)) {
            // If response.data is already an array
            ordersList = ordersRes.data;
          } else if (ordersRes.data.orders && Array.isArray(ordersRes.data.orders)) {
            // If response.data has an orders property that's an array
            ordersList = ordersRes.data.orders;
          }
          
          
          // Take the 5 most recent orders
          const recent = ordersList.slice(0, 5);
          
          setRecentOrders(recent);
        }
      } catch (err) {
        console.error('❌ Dashboard fetch error:', err);
        console.error('Error response:', err.response?.data);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'confirmed':
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'shipped':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  if (authLoading || loading) {
    return (
      <div className="flex flex-col min-h-screen">
        <Navbar />
        <StorefrontBanner />
        <div className="flex justify-center items-center flex-grow">
          <LoadingSpinner size="lg" />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <StorefrontBanner />
      <div className="pt-16 lg:pt-0">
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-8">
            {t('dashboard.welcome')}, {user?.profile?.businessName || user?.email || 'Business'}!
          </h1>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div onClick={() => navigate('/my-orders')} className="bg-white p-6 rounded-lg shadow-md cursor-pointer hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{t('dashboard.stats.totalOrders')}</p>
                  <p className="text-3xl font-bold text-gray-900">{analytics.totalOrders}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{t('dashboard.stats.totalSpent')}</p>
                  <p className="text-3xl font-bold text-gray-900">€{analytics.totalSpent?.toFixed(2) || '0.00'}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>

            <div onClick={() => navigate('/wholesalers')} className="bg-white p-6 rounded-lg shadow-md cursor-pointer hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{t('dashboard.stats.activeSuppliers')}</p>
                  <p className="text-3xl font-bold text-gray-900">{analytics.totalSuppliers || 0}</p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Link
              to="/wholesalers"
              className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-6 rounded-lg shadow-md hover:from-blue-600 hover:to-blue-700 transition-all transform hover:scale-105"
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold">{t('dashboard.browseProducts')}</h3>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
              </div>
              <p className="text-sm text-blue-100">{t('dashboard.exploreCatalog')}</p>
            </Link>
            
            <Link
              to="/my-orders"
              className="bg-gradient-to-br from-purple-500 to-purple-600 text-white p-6 rounded-lg shadow-md hover:from-purple-600 hover:to-purple-700 transition-all transform hover:scale-105"
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold">{t('dashboard.viewOrders')}</h3>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <p className="text-sm text-purple-100">{t('dashboard.trackHistory')}</p>
            </Link>

            <Link
              to="/business/analytics"
              className="bg-gradient-to-br from-green-500 to-green-600 text-white p-6 rounded-lg shadow-md hover:from-green-600 hover:to-green-700 transition-all transform hover:scale-105"
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold">{t('dashboard.analytics')}</h3>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <p className="text-sm text-green-100">{t('dashboard.viewInsights')}</p>
            </Link>
            
            <Link
              to="/business/profile"
              className="bg-gradient-to-br from-orange-500 to-orange-600 text-white p-6 rounded-lg shadow-md hover:from-orange-600 hover:to-orange-700 transition-all transform hover:scale-105"
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold">{t('dashboard.manageProfile')}</h3>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <p className="text-sm text-orange-100">{t('dashboard.updateInfo')}</p>
            </Link>
          </div>

          {/* Recent Orders */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">{t('dashboard.recentOrders')}</h2>
              <Link
                to="/my-orders"
                className="text-blue-600 hover:text-blue-700 font-medium text-sm"
              >
                {t('dashboard.viewAll')} →
              </Link>
            </div>

            {recentOrders.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                </div>
                <p className="text-gray-500 mb-4">{t('dashboard.noOrders')}</p>
                <button
                  onClick={() => navigate('/wholesalers')}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  {t('dashboard.startShopping')}
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {recentOrders.map((order) => (
                  <Link
                    key={order._id}
                    to="/my-orders"
                    className="flex justify-between items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors block"
                  >
                    <div className="flex-1">
                      <p className="font-semibold text-gray-800">{order.orderNumber}</p>
                      <p className="text-sm text-gray-600">
                        {order.wholesalerId?.companyName || 'Unknown Wholesaler'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {format(new Date(order.createdAt), 'MMM d, yyyy')}
                      </p>
                    </div>
                    <div className="text-right mr-4">
                      <p className="font-bold text-gray-800">
                            {order.wholesalerId?.hidePricing
                              ? t('myOrders.pricingHidden', 'Upon agreement')
                              : `€${order.totals?.total?.toFixed(2) || '0.00'}`
                            }
                          </p>
                      <p className="text-xs text-gray-500">{order.items?.length || 0} {t('dashboard.items')}</p>
                    </div>
                    <span className={`px-3 py-1 text-xs font-medium rounded-full capitalize ${getStatusColor(order.status)}`}>
                      {t(`orders:MyOrders.Statuses.${order.status}`)}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default BusinessDashboard;